import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  FileVideo,
  RotateCcw,
  Shuffle,
  Upload,
  Wand2,
  X,
} from 'lucide-react';
import './HookChangerPage.css';
import UniversalLayout from './components/UniversalLayout';
import { useAuthContext } from './components/AuthProvider';
import AuthModal from './components/AuthModal';
import AgentAutoReloadModal from './components/AgentAutoReloadModal';
import {
  generateHookScripts,
  getAgentEligibility,
  initializeProject,
  startAgentCompose,
} from './lib/api';

const MAX_HOOKS = 10;
const MIN_HOOKS = 1;

function buildHookChangerPrompt(hook) {
  return `Use the attached source video as the reference and rebuild it as a new editable Latted project with a changed opening hook. Use the hook-changer-video skill and follow it exactly, replacing only the opening hook and keeping the rest of the source video untouched.

Template: Hook Changer

New opening hook:
${hook.hookScript}

Creative strategy:
${hook.strategy || 'Improve retention with a stronger first beat.'}`;
}

function clampHookCount(value) {
  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) return MIN_HOOKS;
  return Math.max(MIN_HOOKS, Math.min(MAX_HOOKS, parsed));
}

function isSupportedVideoFile(file) {
  const filename = (file?.name || '').toLowerCase();
  return (
    file?.type?.startsWith('video/') ||
    filename.endsWith('.mp4') ||
    filename.endsWith('.mov') ||
    filename.endsWith('.webm')
  );
}

function HookChangerPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const pendingSubmitRef = useRef(null);
  const {
    user, loading, openAuthModal, closeAuthModal, authModalOpen,
    signInWithGoogle, signInWithEmail,
  } = useAuthContext();

  const [pagePhase, setPagePhase] = useState('input');
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState('');
  const [sourceAspectRatio, setSourceAspectRatio] = useState('9:16');
  const [numHooks, setNumHooks] = useState(3);
  const [hooks, setHooks] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [generatingProjects, setGeneratingProjects] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [showAutoReloadModal, setShowAutoReloadModal] = useState(false);

  useEffect(() => {
    return () => {
      if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    };
  }, [videoPreviewUrl]);

  const isMultiSelect = eligibility?.auto_reload_enabled === true;
  const selectedCount = hooks.filter((hook) => hook.selected).length;
  const canGenerateHooks = Boolean(videoFile) && pagePhase === 'input';

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!isSupportedVideoFile(file)) {
      setSubmitError('Please upload a video file.');
      event.target.value = '';
      return;
    }

    setVideoFile(file);
    setVideoPreviewUrl(URL.createObjectURL(file));
    setUploadedVideoUrl('');
    setHooks([]);
    setGeneratingProjects({});
    setSubmitError(null);
    setPagePhase('input');
    event.target.value = '';
  };

  const handleRemoveVideo = () => {
    setVideoFile(null);
    setVideoPreviewUrl(null);
    setUploadedVideoUrl('');
    setHooks([]);
    setGeneratingProjects({});
    setSubmitError(null);
    setPagePhase('input');
  };

  const handleLoadedMetadata = (event) => {
    const { videoWidth, videoHeight } = event.currentTarget;
    if (!videoWidth || !videoHeight) return;
    setSourceAspectRatio(videoWidth > videoHeight ? '16:9' : '9:16');
  };

  const refreshEligibility = useCallback(async () => {
    if (!user) return null;
    try {
      const nextEligibility = await getAgentEligibility();
      setEligibility(nextEligibility);
      return nextEligibility;
    } catch (err) {
      console.error('Eligibility check failed:', err);
      return null;
    }
  }, [user]);

  const handleGenerateHooks = useCallback(async () => {
    if (!videoFile) return;
    setPagePhase('generating_hooks');
    setSubmitError(null);

    try {
      const result = await generateHookScripts({
        videoFile,
        numHooks,
      });
      const nextEligibility = await refreshEligibility();
      const canMulti = nextEligibility?.auto_reload_enabled === true;
      setUploadedVideoUrl(result.videoUrl);
      setHooks((result.hooks || []).map((hook, index) => ({
        ...hook,
        selected: canMulti || index === 0,
        expanded: index === 0,
      })));
      setPagePhase('showing_hooks');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Failed to generate hooks:', err);
      setSubmitError('Failed to generate hooks. Please try again.');
      setPagePhase('input');
    }
  }, [numHooks, refreshEligibility, videoFile]);

  const handleToggleHook = (idx) => {
    setHooks((prev) => prev.map((hook, index) => {
      if (index === idx) return { ...hook, selected: !hook.selected };
      if (!isMultiSelect) return { ...hook, selected: false };
      return hook;
    }));
  };

  const handleToggleExpand = (idx) => {
    setHooks((prev) => prev.map((hook, index) => (
      index === idx ? { ...hook, expanded: !hook.expanded } : hook
    )));
  };

  const handleUpdateHookScript = (idx, value) => {
    setHooks((prev) => prev.map((hook, index) => (
      index === idx ? { ...hook, hookScript: value } : hook
    )));
  };

  const generateSingleProjectAndNavigate = useCallback(async (hook) => {
    if (!uploadedVideoUrl) {
      throw new Error('Source video is still uploading. Please generate hooks again.');
    }

    const projectId = Math.random().toString(36).substring(2, 15);
    const prompt = buildHookChangerPrompt(hook);

    await initializeProject(projectId, prompt, sourceAspectRatio);
    await startAgentCompose(
      projectId,
      prompt,
      [],
      'pro',
      [uploadedVideoUrl],
      'hook_changer_template',
      { analyzeVideoAttachments: true }
    );

    navigate(`/project/${projectId}`, {
      state: { agentMode: true, promptText: prompt },
    });
  }, [navigate, sourceAspectRatio, uploadedVideoUrl]);

  const generateSingleProject = useCallback(async (hook, idx) => {
    const projectId = Math.random().toString(36).substring(2, 15);
    const prompt = buildHookChangerPrompt(hook);
    setGeneratingProjects((prev) => ({ ...prev, [idx]: { status: 'initializing', projectId } }));

    try {
      await initializeProject(projectId, prompt, sourceAspectRatio);
      setGeneratingProjects((prev) => ({ ...prev, [idx]: { status: 'composing', projectId } }));
      await startAgentCompose(
        projectId,
        prompt,
        [],
        'pro',
        [uploadedVideoUrl],
        'hook_changer_template',
        { analyzeVideoAttachments: true }
      );
      setGeneratingProjects((prev) => ({ ...prev, [idx]: { status: 'done', projectId } }));
    } catch (err) {
      console.error(`Hook video generation failed for hook ${idx}:`, err);
      setGeneratingProjects((prev) => ({
        ...prev,
        [idx]: { status: 'error', projectId, error: 'Failed' },
      }));
    }
  }, [sourceAspectRatio, uploadedVideoUrl]);

  const handleGenerateVideos = useCallback(async () => {
    const selectedHooks = hooks
      .map((hook, idx) => ({ ...hook, idx }))
      .filter((hook) => hook.selected && hook.hookScript.trim());

    if (selectedHooks.length === 0) return;

    if (!user && !loading) {
      pendingSubmitRef.current = 'generate_videos';
      openAuthModal();
      return;
    }

    const nextEligibility = await refreshEligibility();
    if (nextEligibility && !nextEligibility.allowed && nextEligibility.reason === 'auto_reload_required') {
      setShowAutoReloadModal(true);
      return;
    }

    setSubmitError(null);
    setPagePhase('generating_videos');
    const canMulti = nextEligibility?.auto_reload_enabled === true;

    if (canMulti) {
      await Promise.allSettled(
        selectedHooks.map((hook) => generateSingleProject(hook, hook.idx))
      );
      return;
    }

    try {
      await generateSingleProjectAndNavigate(selectedHooks[0]);
    } catch (err) {
      console.error('Hook video generation failed:', err);
      setSubmitError('Failed to start generation. Please try again.');
      setPagePhase('showing_hooks');
    }
  }, [
    generateSingleProject,
    generateSingleProjectAndNavigate,
    hooks,
    loading,
    openAuthModal,
    refreshEligibility,
    user,
  ]);

  const handleBackToInput = () => {
    setHooks([]);
    setGeneratingProjects({});
    setSubmitError(null);
    setPagePhase('input');
  };

  const handleEnableAutoReloadAndContinue = useCallback(async () => {
    setShowAutoReloadModal(false);
    await handleGenerateVideos();
  }, [handleGenerateVideos]);

  useEffect(() => {
    if (!user || !pendingSubmitRef.current) return;
    const action = pendingSubmitRef.current;
    pendingSubmitRef.current = null;
    if (action === 'generate_videos') {
      handleGenerateVideos();
    }
  }, [handleGenerateVideos, user]);

  return (
    <UniversalLayout>
      <div className="hook-content">
        <main className="hook-main">
          <header className="hook-header">
            <span className="hook-eyebrow">Template</span>
            <h1 className="hook-title">
              Hook <span className="hook-title-em">Changer</span>
            </h1>
            <p className="hook-subtitle">
              Upload an existing video, generate new first-second hooks, then rebuild
              variants with the original structure and visual style.
            </p>
          </header>

          {(pagePhase === 'input' || pagePhase === 'generating_hooks') && (
            <div className="hook-config-grid">
              <section className="hook-panel hook-panel--form">
                <div className="hook-section-header">
                  <span className="hook-section-number">01</span>
                  <div>
                    <h2>Source video</h2>
                    <p>The LLM reads this once to generate replacement hooks.</p>
                  </div>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm"
                  className="hook-file-input"
                  onChange={handleFileChange}
                />

                {videoPreviewUrl ? (
                  <div className="hook-video-preview">
                    <video
                      src={videoPreviewUrl}
                      controls
                      onLoadedMetadata={handleLoadedMetadata}
                    />
                    <div className="hook-video-meta">
                      <FileVideo size={16} strokeWidth={1.8} aria-hidden="true" />
                      <span>{videoFile?.name}</span>
                      <button type="button" onClick={handleRemoveVideo} aria-label="Remove video">
                        <X size={15} strokeWidth={1.9} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button type="button" className="hook-upload-zone" onClick={handleUploadClick}>
                    <span className="hook-upload-icon" aria-hidden="true">
                      <Upload size={24} strokeWidth={1.7} />
                    </span>
                    <span className="hook-upload-title">Upload a video</span>
                    <span className="hook-upload-hint">MP4, MOV, or WebM up to 500MB</span>
                  </button>
                )}

                <div className="hook-control-row">
                  <label htmlFor="hook-count">Number of new hooks</label>
                  <input
                    id="hook-count"
                    type="number"
                    min={MIN_HOOKS}
                    max={MAX_HOOKS}
                    value={numHooks}
                    onChange={(event) => setNumHooks(clampHookCount(event.target.value))}
                  />
                </div>

                <button
                  type="button"
                  className="hook-btn-submit"
                  disabled={!canGenerateHooks || pagePhase === 'generating_hooks'}
                  onClick={handleGenerateHooks}
                >
                  {pagePhase === 'generating_hooks' ? (
                    <>
                      <span className="hook-spinner" />
                      Generating hooks
                    </>
                  ) : (
                    <>
                      <Wand2 size={17} strokeWidth={1.9} />
                      Generate Hooks
                    </>
                  )}
                </button>

                {submitError && <span className="hook-error">{submitError}</span>}
              </section>

              <aside className="hook-panel hook-panel--aside">
                <div className="hook-aside-icon" aria-hidden="true">
                  <Shuffle size={22} strokeWidth={1.7} />
                </div>
                <h2>What this does</h2>
                <p>
                  Hook Changer keeps the original video as the source of truth, creates
                  fresh opening angles, and sends each selected hook to the general
                  video agent as a rebuild instruction.
                </p>
                <div className="hook-aside-steps">
                  <span>Upload source</span>
                  <span>Generate hooks</span>
                  <span>Rebuild variants</span>
                </div>
              </aside>
            </div>
          )}

          {(pagePhase === 'showing_hooks' || pagePhase === 'generating_videos') && (
            <section className="hook-results">
              <div className="hook-results-header">
                <div>
                  <h2>
                    Generated hooks
                    <span>{hooks.length}</span>
                  </h2>
                  <p>
                    {isMultiSelect
                      ? 'Select any hooks to rebuild in parallel.'
                      : 'Select one hook to rebuild. Auto-reload credits enable batch generation.'}
                  </p>
                </div>
                {pagePhase === 'showing_hooks' && (
                  <button type="button" className="hook-btn-secondary" onClick={handleBackToInput}>
                    <RotateCcw size={15} strokeWidth={1.9} />
                    Change source
                  </button>
                )}
              </div>

              <div className="hook-list">
                {hooks.map((hook, idx) => {
                  const project = generatingProjects[idx];
                  return (
                    <article
                      key={`${hook.title}-${idx}`}
                      className={`hook-card${hook.selected ? ' hook-card--selected' : ''}${project ? ' hook-card--generating' : ''}`}
                    >
                      <div className="hook-card-header">
                        {pagePhase === 'showing_hooks' && (
                          <button
                            type="button"
                            className={`hook-select${hook.selected ? ' hook-select--active' : ''}`}
                            onClick={() => handleToggleHook(idx)}
                            aria-label={hook.selected ? 'Deselect hook' : 'Select hook'}
                          >
                            {hook.selected && <Check size={13} strokeWidth={3} />}
                          </button>
                        )}
                        <div className="hook-card-title-wrap">
                          <h3>{hook.title}</h3>
                          {hook.strategy && <p>{hook.strategy}</p>}
                        </div>
                        {project && (
                          <div className="hook-project-status">
                            {(project.status === 'initializing' || project.status === 'composing') && (
                              <span className="hook-project-spinner" />
                            )}
                            {project.status === 'done' && (
                              <button
                                type="button"
                                onClick={() => navigate(`/project/${project.projectId}`)}
                              >
                                Go to Project
                              </button>
                            )}
                            {project.status === 'error' && <span>Failed</span>}
                          </div>
                        )}
                      </div>

                      {hook.expanded ? (
                        <textarea
                          className="hook-script-input"
                          value={hook.hookScript}
                          onChange={(event) => handleUpdateHookScript(idx, event.target.value)}
                          rows={5}
                          disabled={pagePhase === 'generating_videos'}
                        />
                      ) : (
                        <p className="hook-script-preview">
                          {hook.hookScript.slice(0, 220)}
                          {hook.hookScript.length > 220 ? '...' : ''}
                        </p>
                      )}

                      <button
                        type="button"
                        className="hook-expand-btn"
                        onClick={() => handleToggleExpand(idx)}
                      >
                        {hook.expanded ? 'Collapse script' : 'Edit full script'}
                      </button>
                    </article>
                  );
                })}
              </div>

              {pagePhase === 'showing_hooks' && (
                <div className="hook-submit-area">
                  <button
                    type="button"
                    className="hook-btn-submit"
                    disabled={selectedCount === 0}
                    onClick={handleGenerateVideos}
                  >
                    {selectedCount === 0
                      ? 'Select hooks to generate'
                      : selectedCount === 1
                        ? 'Generate 1 Video'
                        : `Generate ${selectedCount} Videos`}
                  </button>
                  {submitError && <span className="hook-error">{submitError}</span>}
                </div>
              )}

              {pagePhase === 'generating_videos' && (
                <div className="hook-submit-area">
                  <p className="hook-generating-label">
                    <span className="hook-project-spinner" />
                    Starting video generations
                  </p>
                </div>
              )}
            </section>
          )}
        </main>
      </div>

      <AuthModal
        isOpen={!loading && !user && authModalOpen}
        onClose={closeAuthModal}
        onSignIn={() => signInWithGoogle()}
        onEmailSignIn={signInWithEmail}
      />

      <AgentAutoReloadModal
        isOpen={showAutoReloadModal}
        onClose={() => setShowAutoReloadModal(false)}
        onEnableAutoReload={handleEnableAutoReloadAndContinue}
        onCreateBlankProject={() => {
          setShowAutoReloadModal(false);
          const firstSelected = hooks.find((hook) => hook.selected);
          if (firstSelected) {
            generateSingleProjectAndNavigate(firstSelected).catch((err) => {
              console.error('Fallback hook generation failed:', err);
              setSubmitError('Failed to start generation.');
              setPagePhase('showing_hooks');
            });
          }
        }}
      />
    </UniversalLayout>
  );
}

export default HookChangerPage;
